import { formatMoney } from '@betterment/core';

import { ActivityItemFragment } from '../../../graphql/fragments/activityItem.generated';
import { pluralize } from '../../../utils/strings/strings';

function getActivityValue(item: ActivityItemFragment): string {
  const noData = '—';

  switch (item.__typename) {
    case 'ActivityItemOther':
    case 'ActivityItemDeposit':
    case 'ActivityItemWithdrawal':
    case 'ActivityItemFee':
      return item.amount ? formatMoney(item.amount) : noData;
    case 'ActivityItemTradeGroup':
      return `${Array(item.sales).length + Array(item.purchases).length} trades`;
    case 'ActivityItemTransferGroup':
    case 'ActivityItemAllocationChange':
      return noData;
    case 'ActivityItemOrder': {
      const amount = item.amountTraded ?? item.amountRequested;
      const shares = item.sharesTraded ?? item.sharesRequested;
      if (amount) {
        return formatMoney(amount);
      }
      if (shares) {
        const parsedShares = parseFloat(shares);
        if (Number.isFinite(parsedShares)) {
          return pluralize(parsedShares, 'share');
        }
      }
      return noData;
    }
    default:
      throw new Error(`Unhandled activity item type: ${(item as { __typename: string }).__typename}`);
  }
}

export const extractActivityData = (item: ActivityItemFragment) => {
  const { subAccount, description } = item;

  const memo = 'memo' in item ? item.memo : undefined;

  const dateFormat = { month: 'short', day: 'numeric', year: 'numeric' } as const;
  const timeFormat = { hour: 'numeric', minute: '2-digit', hour12: true } as const;
  const timestamp = new Date(item.timestamp);
  const date = timestamp.toLocaleDateString('en-US', dateFormat);
  const time = timestamp.toLocaleTimeString('en-US', timeFormat);

  const envelopeName = subAccount.envelope.purpose?.name;
  const accountName = subAccount.legalAccount.displayName;
  const latestStatus = [...item.statuses].reverse().find(status => status.timestamp);
  const status = latestStatus?.stage || null;

  const subAccountName = subAccount?.name;

  const { users } = subAccount;

  return {
    memo,
    time,
    date,
    envelopeName,
    accountName,
    subAccountName,
    description,
    status,
    users,
    activityValue: getActivityValue(item),
  };
};
